import Phaser from 'phaser';
import {
  preloadActorManifest,
  prepareActorFromCache,
  queueActorTextures,
  type ActorAsset,
  type PreparedActor,
} from './actor-clipset';

// NPC rig registry (Order 16.6) — which rig asset an id in a Scene's `npcs`
// field names. This closes, for NPCs first, the actor-identity gap the 16.5
// report flagged ("spawns pick faction, not WHICH rig"): a Scene names a rig by
// id; this module says what that id loads.
//
// NPC rigs ride the exact same pipeline as the combat actor — a DeformClipSet
// JSON naming part PNGs, two loader runs, feet-origin normalization — with one
// difference: the required clip set. A standing NPC bakes 'idle' and 'talk';
// walk/jump/attack/hit are the combat actor's vocabulary, and requiring them
// of a figure whose stillness is the acting would force fake content.

/** What a standing NPC's clip set must carry. */
export const NPC_CLIPS = ['idle', 'talk'] as const;

/** The woman in the Courtyard plate — the first NPC (arcade_playable_cinema_v0,
 *  "the minimal rig — the standing NPC"). figureHeightPx sizes her against the
 *  courtyard's door line; tuned by eye like the pig's. Matt's calibration
 *  2026-07-11: she plays downstage (low z), so she is sized as a foreground
 *  figure — the attention read (her head turning) has to survive the wide shot. */
export const WOMAN: ActorAsset = {
  key: 'woman',
  url: 'actor/woman/clip-set.json',
  figureHeightPx: 205,
};

/** Rig id (as a Scene's npcs[].rig names it) → asset. */
export const NPC_RIGS: Record<string, ActorAsset> = {
  [WOMAN.key]: WOMAN,
};

export function resolveNpcRig(id: string): ActorAsset {
  const rig = NPC_RIGS[id];
  if (!rig) throw new Error(`npc: unknown rig '${id}' (have: ${Object.keys(NPC_RIGS).join(', ')})`);
  return rig;
}

/** Registry key the prepared rig is published under. */
export const preparedNpcKey = (rigId: string) => `npc:prepared:${rigId}`;

/** Queue every registered NPC rig manifest. PreloadScene.preload — the registry
 *  is small and static, so all rigs preload; which ones BOOT (textures) is the
 *  active scene's business. Skips rigs already prepared this run. */
export function preloadNpcManifests(scene: Phaser.Scene): void {
  for (const rig of Object.values(NPC_RIGS)) {
    if (!scene.registry.get(preparedNpcKey(rig.key))) preloadActorManifest(scene, rig);
  }
}

/** Prepare (validate + normalize) every rig fetched above and publish it.
 *  PreloadScene.create. Same once-per-run rule as the combat actor: prepare
 *  mutates the cached JSON, so a transition reuses the first run's result. */
export function prepareNpcs(scene: Phaser.Scene): void {
  for (const rig of Object.values(NPC_RIGS)) {
    const key = preparedNpcKey(rig.key);
    if (scene.registry.get(key)) continue;
    scene.registry.set(key, prepareActorFromCache(scene, rig, NPC_CLIPS));
  }
}

/** Queue the part textures for the rigs the active scene actually uses.
 *  BootScene.preload. */
export function queueNpcTextures(scene: Phaser.Scene, rigIds: readonly string[]): void {
  for (const id of new Set(rigIds)) {
    const rig = resolveNpcRig(id);
    const prepared = scene.registry.get(preparedNpcKey(id)) as PreparedActor | undefined;
    if (!prepared) throw new Error(`npc rig '${id}': not prepared — PreloadScene must run first`);
    if (!scene.textures.exists(prepared.textureKeys[0])) queueActorTextures(scene, rig, prepared);
  }
}

/** The prepared rig for a Scene's npc row (SceneRunner.create). */
export function loadedNpcRig(scene: Phaser.Scene, rigId: string): PreparedActor {
  const prepared = scene.registry.get(preparedNpcKey(rigId)) as PreparedActor | undefined;
  if (!prepared) throw new Error(`npc rig '${rigId}': not prepared — PreloadScene must run first`);
  return prepared;
}
